/**
 * Usage Store
 *
 * Tracks feature usage counts for the current billing period.
 * Syncs with server via get_usage_summary RPC.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '@/lib/state/ssr-storage';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { rpc } from '@/lib/supabase/rpc';
import { reportError } from '@/lib/errors';

// ============================================================================
// Helpers
// ============================================================================

export function getCurrentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ============================================================================
// Types
// ============================================================================

interface UsageState {
  currentUsage: Record<string, number>;
  currentPeriod: string;
  isLoading: boolean;

  fetchUsage: () => Promise<void>;
  incrementLocal: (featureKey: string) => void;
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  currentUsage: {} as Record<string, number>,
  currentPeriod: getCurrentPeriod(),
  isLoading: false,
};

// ============================================================================
// Store
// ============================================================================

export const useUsageStore = create<UsageState>()(
  persist(
    (set, get) => ({
      ...initialState,

      fetchUsage: async () => {
        if (!isSupabaseConfigured()) return;

        set({ isLoading: true });

        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            set({ isLoading: false });
            return;
          }

          const period = getCurrentPeriod();

          // Fetch current month usage
          const { data: monthlyData, error: monthlyError } = await rpc('get_usage_summary', {
            p_user_id: user.id,
            p_period: period,
          });

          // A FAILED read is not an EMPTY read. Writing `?? {}` here on error
          // wiped the persisted counters every time the call failed — which it
          // did on every call for ten days, after `get_usage_summary` lost its
          // EXECUTE grant in the RPC privilege lockdown (42501). UsageIndicator
          // then showed "0/N used" forever and never reached "Limit reached",
          // so users hit the server-side cap with no warning. On failure the
          // last known-good counters stand.
          // Guarded by state/__tests__/usage-store-read-failure.test.ts.
          if (monthlyError) {
            reportError(monthlyError, { context: 'UsageStore.fetchUsage.monthly', level: 'warning' });
            set({ isLoading: false });
            return;
          }

          // (B23: the lifetime fetch was removed — basic_scoring is a MONTHLY
          // cap for all tiers since 2026-07-04; no feature uses lifetime usage.)

          // `{}` here is a genuine server answer — no usage yet this period —
          // and MUST still clear the store, or a new billing period would
          // inherit the previous one's counters.
          set({
            currentUsage: (monthlyData as Record<string, number> | null) ?? {},
            currentPeriod: period,
            isLoading: false,
          });
        } catch (err) {
          reportError(err, { context: 'UsageStore.fetchUsage' });
          set({ isLoading: false });
        }
      },

      incrementLocal: (featureKey: string) => {
        const period = getCurrentPeriod();
        const state = get();

        // If period changed, reset current usage
        if (state.currentPeriod !== period) {
          set({
            currentUsage: { [featureKey]: 1 },
            currentPeriod: period,
          });
          return;
        }

        set({
          currentUsage: {
            ...state.currentUsage,
            [featureKey]: (state.currentUsage[featureKey] ?? 0) + 1,
          },
        });
      },

      reset: () => set(initialState),
    }),
    {
      name: 'inverio-usage',
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        currentUsage: state.currentUsage,
        currentPeriod: state.currentPeriod,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && state.currentPeriod !== getCurrentPeriod()) {
          // New month — clear monthly usage
          state.currentUsage = {};
          state.currentPeriod = getCurrentPeriod();
        }
      },
    }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

export function useUsageCount(featureKey: string): number {
  return useUsageStore((s) => s.currentUsage[featureKey] ?? 0);
}
